// src/components/instructor/OrgOperationsWorkspace.js
//
// Pass 49B — THE single primary operations workspace: "Bookings & Availability". Consolidates
// the former competing Schedule + Bookings products into one screen on ONE realtime data
// source (useOrgOperations), with a canonical season selector and the five primary views
// (Calendar · Day · Week · Season · Unassigned). Audit history / Sync status are SECONDARY
// actions, not top-level products. Historical seasons are read-only.

import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import MotusIcon from '../shared/MotusIcon';
import { useAuth } from '../../AuthContext';
import { useSelectedOrganizationContext } from '../../utils/organizationSelection';
import { useOrgOperations } from '../../hooks/useOrgOperations';
import { useOrgAreaConfig } from '../../hooks/useOrgAreaConfig';
import { bookingMatchesAreaFilter, AREA_FILTER_ALL, AREA_FILTER_UNRESOLVED } from '../../utils/operatingAreas';
import {
  deriveWorkspaceData, weekStartOf, scopeRequestsToSeason, clampDateToSeason, SEASONS, seasonLabel,
} from '../../utils/operationsWorkspace';
import { resolveInstructorCategory } from '../../utils/instructorCategory';
import { zonedDateKey, wallClockToInstant, zonedShiftDays } from '../../utils/opsTime';
import { resolveOrgSeasonId, resolveOrgTimezone } from '../../utils/organizationRuntime';
import { ActivityPaymentLegend, fmtTime } from './ops/opsShared';
import DayOps from './ops/DayOps';
import WeekOps from './ops/WeekOps';
import UnassignedQueue from './ops/UnassignedQueue';
import RequestsView from './ops/RequestsView';
import { synthesizeCasesFromBookings } from '../../utils/bookingCase';
import { changeCaseStage, materializeMissingCaseBookings } from '../../utils/bookingCaseWriters';
import { createSourceIntake, convertIntakeToBookingCase, ignoreSourceIntake, newIntakeId } from '../../utils/sourceIntakeWriters';
import { buildClientIdentityIndex } from '../../utils/clientIdentity';
import { getOrCreateLessonInstanceForBooking } from '../../utils/lessonHandoff';
import { db } from '../../firebase';
import AddWorkModal from './ops/AddWorkModal';
import NewBookingWizard from './ops/NewBookingWizard';
import BookingDetailModal from './ops/BookingDetailModal';
import OrgBookingsCRM from './OrgBookingsCRM';
import SeasonOverview from './SeasonOverview';
import { SyncStatusPanel, AuditHistoryPanel } from './OpsSchedulePanel';

const V = { t1: '#1A1917', t2: '#6B6860', t3: '#9E9B95', border: '#E2E0DB', s2: '#F2F1EE', green: '#1D9E75', amber: '#C97D10' };
const F = "'DM Sans',sans-serif";

// Pass 50A §5: Requests (parent booking cases) is the operational-sales starting point.
// Pass 58G — scheduling-first ordering (Calendar/Day/Week/Unassigned/Season/Requests); audit/sync are
// secondary tools, not primary navigation.
const PRIMARY_VIEWS = [['calendar', 'Calendar'], ['day', 'Day'], ['week', 'Week'], ['unassigned', 'Unassigned'], ['season', 'Season'], ['requests', 'Requests']];
const isEmulator = () => process.env.REACT_APP_USE_EMULATOR === 'true';

function pill(active) {
  return {
    padding: '6px 14px', borderRadius: 9, border: `1px solid ${active ? V.t1 : V.border}`,
    background: active ? V.t1 : '#fff', color: active ? '#fff' : V.t2,
    fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: F,
  };
}

export default function OrgOperationsWorkspace({ onSelectInstructor, initialView = 'calendar', initialDate = null }) {
  const { userData, currentUser } = useAuth();
  const { selectedOrganization } = useSelectedOrganizationContext(userData);
  const orgId = selectedOrganization?.organizationId || userData?.orgId || '';

  // Pass 82C.2 §2 — the current season + wall-clock timezone are the ORGANIZATION's, not a platform-global SAS default.
  const currentSeasonId = resolveOrgSeasonId(selectedOrganization);
  const orgTz = resolveOrgTimezone(selectedOrganization);

  // Pass 82E — the org's operating-area config + the resolver context that reads/filters share (one config truth).
  const { areas, serviceLocations, meetingPoints, resolverCtx } = useOrgAreaConfig(orgId, selectedOrganization);
  // Selected operations area filter ('all' | '<operatingAreaId>' | 'unresolved'), persisted per ORG (never leaks tenants).
  const areaKey = `motus-ops-area::${orgId}`;
  const [areaFilter, setAreaFilter] = useState(AREA_FILTER_ALL);
  useEffect(() => {
    try { const v = orgId ? window.localStorage.getItem(areaKey) : ''; setAreaFilter(v || AREA_FILTER_ALL); } catch (e) { setAreaFilter(AREA_FILTER_ALL); }
  }, [areaKey, orgId]);
  const selectArea = (v) => { setAreaFilter(v); try { window.localStorage.setItem(areaKey, v); } catch (e) { /* ignore */ } };

  const [seasonId, setSeasonId] = useState(currentSeasonId);
  // Pass 87.2 — THE ONE shared operations session (provider-aware). The current season uses the fast
  // scoped realtime feed; selecting a historical season transparently escalates to the lazily-loaded
  // all-seasons snapshot so archived seasons stay fully reachable. `fromCache` drives a subtle
  // "Updating…" affordance on a warm return instead of a blank loading gate.
  const { loading, error, lastUpdatedAt, fromCache, bookings, members, bookingRequests, sourceIntakes, availabilitySlots, conflicts } = useOrgOperations(orgId, { seasonId, currentSeasonId });
  // §4 the season selector must ALWAYS be able to represent the org's current season, even if it is not one of the
  // known historical SAS seasons. Merge it in without redesigning season administration.
  const seasonOptions = useMemo(() => [...new Set([...SEASONS, currentSeasonId])].filter(Boolean).sort(), [currentSeasonId]);
  const [view, setView] = useState(PRIMARY_VIEWS.some(([k]) => k === initialView) ? initialView : 'calendar');
  const [secondary, setSecondary] = useState(''); // '' | 'audit' | 'sync'
  // Pass 58F5 §15 — the normal operational schedule uses the confirmed current roster. Roster-review
  // (data-cleanup) machinery moved OUT of the primary Bookings flow to Team → Roster Review.
  const includeReview = false;
  // Pass 52 §5 — an Overview deep-link can seed the Day date (e.g. "Tomorrow's Lessons").
  const [selectedDate, setSelectedDate] = useState(initialDate || null);
  const [weekAnchor, setWeekAnchor] = useState(null);
  const [showAddWork, setShowAddWork] = useState(false);
  const [showNewBooking, setShowNewBooking] = useState(false);
  // Pass 58A — the ONE booking-detail surface: any view opens the same modal (BookingDetailActions host).
  const [detailBooking, setDetailBooking] = useState(null);

  // §3 useState(initial) does NOT react to prop change — when the operator switches organizations (new org id /
  // different current season) explicitly reset the selected season to the NEW org's current season and clear any
  // stale date/week anchors from the previous tenant. Switching back restores that org's current season.
  const orgResetKey = `${orgId}::${currentSeasonId}`;
  const lastOrgResetKey = useRef(orgResetKey);
  useEffect(() => {
    if (lastOrgResetKey.current === orgResetKey) return;
    lastOrgResetKey.current = orgResetKey;
    setSeasonId(currentSeasonId);
    setSelectedDate(null);
    setWeekAnchor(null);
    setSecondary('');
  }, [orgResetKey, currentSeasonId]);

  const wd = useMemo(
    () => deriveWorkspaceData({ bookings, members, seasonId, date: selectedDate, includeReview, currentSeasonId, timeZone: orgTz }),
    [bookings, members, seasonId, selectedDate, includeReview, currentSeasonId, orgTz],
  );
  // §1 P0: resolve readOnly BEFORE any expression uses it (const has a TDZ — referencing it
  // earlier throws "Cannot access 'readOnly' before initialization" at runtime).
  const readOnly = wd.readOnly;
  // Pass 82E §28 — apply the operating-area filter to the season slices the views render. Uses the SAME resolver as
  // everything else (explicit → source-config → bounded legacy default → unresolved); 'all' shows everything and
  // 'unresolved' surfaces records that resolve to no area (never hidden). Canonical availability is untouched — this is
  // a VISIBILITY filter only.
  const areaBookings = useMemo(
    () => wd.seasonBookings.filter((b) => bookingMatchesAreaFilter(b, areaFilter, resolverCtx)),
    [wd.seasonBookings, areaFilter, resolverCtx],
  );
  const areaUnassigned = useMemo(
    () => wd.unassigned.filter((u) => bookingMatchesAreaFilter(u.booking || u, areaFilter, resolverCtx)),
    [wd.unassigned, areaFilter, resolverCtx],
  );
  // Pass 82E — the operating area a NEW booking/work should default to: the currently-selected area when a specific one
  // is filtered, else the org's default area. The forms REQUIRE an explicit area (never a silent Niseko default).
  const isSpecificArea = areaFilter && areaFilter !== AREA_FILTER_ALL && areaFilter !== AREA_FILTER_UNRESOLVED;
  const defaultAreaForNew = isSpecificArea ? areaFilter : (areas.find((a) => a.isDefault) || (areas.filter((a) => a.status === 'active').length === 1 ? areas.filter((a) => a.status === 'active')[0] : null) || {}).operatingAreaId || '';
  // Pass 64 — the actor context every source-intake writer threads (owner/admin/ops; fail-closed otherwise).
  const intakeCtx = () => ({ userData, organizationId: orgId, actorUserId: currentUser?.uid || '', actorName: userData?.name || userData?.displayName || '' });
  // Pass 65 — case → schedule deep-link. ONE navigation mechanism: internal view/date state is the source
  // of truth (immediate, no remount), mirrored to the URL (?view/?date) so a copied link resolves the same
  // schedule context. `highlightBookingId` lets the target surface spotlight the specific booking.
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [highlightBookingId, setHighlightBookingId] = useState('');
  // Pass 66 — booking → lessonInstance handoff. Explicit action from the booking detail: get-or-create the
  // ONE deterministic booking-linked lesson (idempotent), then open the existing Lesson Builder surface.
  // Authorization threads the assigned-instructor's canonical member id (never the Auth uid) + owner/admin/ops.
  const startLessonForBooking = async (booking) => {
    const me = (wd.seasonMembers || []).find((m) => m.authUserId && m.authUserId === currentUser?.uid);
    const { lessonId } = await getOrCreateLessonInstanceForBooking(db, booking, {
      userData, organizationId: orgId, actorInstructorId: me?.userId || '', actorAuthUserId: currentUser?.uid || '', readOnly,
    });
    setDetailBooking(null);
    navigate(`/instructor/lesson-builder?lessonId=${encodeURIComponent(lessonId)}`);
  };
  const [openCaseId, setOpenCaseId] = useState(''); // Pass 65 — booking → originating enquiry traceback target
  const openSchedule = ({ view: v = 'day', date, bookingId = '' } = {}) => {
    setSecondary('');
    setView(v);
    if (date) setSelectedDate(wallClockToInstant(date, '12:00', orgTz));
    setHighlightBookingId(bookingId);
    const next = new URLSearchParams(searchParams);
    next.set('view', v);
    if (date) next.set('date', date); else next.delete('date');
    setSearchParams(next, { replace: true }); // mirror to URL (no PII — view/date/no booking payload)
  };
  const dayDate = selectedDate || wd.selectedDate || new Date();
  const weekAnchorDate = weekAnchor || wd.selectedDate || new Date();
  // §1/§2 the Calendar/Season initial date is season-aware: historical → the season's real
  // first date; current → the season's first scheduled operational day (or today if none).
  const calendarInitialDate = wd.dateRange?.start || (readOnly ? null : new Date());

  // SeasonOverview expects { id, name, category } keyed by instructorId (=userId).
  const seasonInstructors = useMemo(
    () => wd.seasonMembers.map((m) => ({
      id: m.userId || m.id,
      name: m.name || m.displayName || m.email || m.userId,
      skiLevel: m.skiLevel, snowboardLevel: m.snowboardLevel,
      category: resolveInstructorCategory(m, { season: seasonId }),
    })).filter((m) => m.id),
    [wd.seasonMembers, seasonId],
  );

  return (
    <div style={{ fontFamily: F, color: V.t1 }}>
      {/* Header: title + environment banner + realtime indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Bookings</h2>
        {/* Pass 58F5 §17 — the internal org id is NOT product copy (the workspace nav already says
            "SAS Niseko · Operations"). Keep only a subtle emulator warning in dev; production shows
            the season + a quiet live indicator instead of a PRODUCTION · sas-niseko badge. */}
        {isEmulator() && (
          <span data-testid="env-banner" style={{ fontSize: 11, fontWeight: 700, color: '#B45309', background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 6, padding: '3px 8px' }}>● LOCAL · EMULATOR — DEMO DATA</span>
        )}
        <span style={{ fontSize: 12, color: V.t2, fontWeight: 600 }}>{seasonLabel(seasonId)}{seasonId === currentSeasonId ? ' · Current' : ''}</span>
        <span data-testid="live-indicator" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: V.t2 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: error ? '#DC2626' : (fromCache ? V.amber : V.green) }} />
          {error ? 'Disconnected' : (fromCache ? 'Updating…' : 'Live')}{lastUpdatedAt ? ` · updated ${fmtTime(lastUpdatedAt)}` : ''}
        </span>
        <div style={{ flex: 1 }} />
        {/* §6 canonical season selector */}
        <select
          data-testid="season-selector"
          value={seasonId}
          onChange={(e) => { setSeasonId(e.target.value); setSelectedDate(null); setWeekAnchor(null); setSecondary(''); }}
          style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${V.border}`, fontWeight: 700, fontSize: 13, background: '#fff' }}
          title="Season"
        >
          {seasonOptions.slice().reverse().map((s) => (
            <option key={s} value={s}>{seasonLabel(s)}{s === currentSeasonId ? ' · current' : ''}</option>
          ))}
        </select>
        {/* Pass 82E §28 — operating-area filter (only when the org has configured areas). All · <areas> · Needs area
            (unresolved records stay VISIBLE, never hidden). Selection persists per org. */}
        {areas.length > 0 && (
          <select
            data-testid="area-selector"
            value={areaFilter}
            onChange={(e) => selectArea(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${V.border}`, fontWeight: 700, fontSize: 13, background: '#fff' }}
            title="Operating area"
          >
            <option value={AREA_FILTER_ALL}>All areas</option>
            {areas.filter((a) => a.status !== 'inactive').map((a) => (
              <option key={a.operatingAreaId} value={a.operatingAreaId}>{a.name}{a.status === 'planned' ? ' · planned' : ''}</option>
            ))}
            <option value={AREA_FILTER_UNRESOLVED}>Needs area</option>
          </select>
        )}
        {!readOnly && (
          <>
            <button data-testid="new-booking-btn" onClick={() => setShowNewBooking(true)} style={{ ...pill(false), background: V.t1, color: '#fff', border: 'none' }}>+ New booking</button>
            <button data-testid="add-work-btn" onClick={() => setShowAddWork(true)} style={{ ...pill(false), background: V.green, color: '#fff', border: 'none' }}>+ Add work</button>
          </>
        )}
      </div>

      {/* Primary view switcher. Pass 58G — Audit history / Sync status are moved OUT of the primary row
          into a restrained right-aligned "Operations tools" group (secondary, not daily navigation). */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        {PRIMARY_VIEWS.map(([k, l]) => (
          <button key={k} data-testid={`view-${k}`} onClick={() => { setView(k); setSecondary(''); }} style={pill(view === k && !secondary)}>
            {l}{k === 'unassigned' && areaUnassigned.length ? ` · ${areaUnassigned.length}` : ''}
          </button>
        ))}
        <div style={{ flex: 1, minWidth: 12 }} />
        <span style={{ fontSize: 11, color: V.t3, fontWeight: 600 }}>Ops tools:</span>
        <button data-testid="view-sync" onClick={() => setSecondary(secondary === 'sync' ? '' : 'sync')} style={{ ...pill(secondary === 'sync'), fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 5 }}><MotusIcon name="activity" size={13} color={secondary === 'sync' ? '#fff' : V.t2} /> Sync status</button>
        <button data-testid="view-audit" onClick={() => setSecondary(secondary === 'audit' ? '' : 'audit')} style={{ ...pill(secondary === 'audit'), fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 5 }}><MotusIcon name="clock" size={13} color={secondary === 'audit' ? '#fff' : V.t2} /> Audit history</button>
      </div>

      {/* Historical read-only banner (§26) — Pass 58G: clean Motus icon, short copy (no emoji). */}
      {readOnly && (
        <div data-testid="readonly-banner" style={{ background: 'rgba(217,119,6,0.10)', border: `1px solid ${V.amber}`, color: V.amber, borderRadius: 10, padding: '9px 14px', marginBottom: 12, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
          <MotusIcon name="lock" size={15} color={V.amber} />
          <span><strong style={{ fontWeight: 800, letterSpacing: 0.3 }}>ARCHIVED SEASON · READ ONLY</strong> — {seasonLabel(seasonId)}. Historical records are preserved and cannot be changed.</span>
        </div>
      )}

      {/* §12 Season owns its own legend (hosted SeasonOverview); the workspace renders the
          generic legend only for Day/Week/Unassigned — so Season shows exactly ONE. */}
      {(view === 'day' || view === 'week' || view === 'unassigned') && !secondary && <ActivityPaymentLegend />}

      {loading && !bookings.length ? (
        <div style={{ padding: 40, textAlign: 'center', color: V.t3 }}>Loading operations…</div>
      ) : secondary === 'audit' ? (
        <AuditHistoryPanel orgId={orgId} nameOf={Object.fromEntries(members.map((m) => [m.userId, m.name || m.displayName || m.userId]))} />
      ) : secondary === 'sync' ? (
        <SyncStatusPanel orgId={orgId} />
      ) : view === 'requests' ? (
        // §5/§6/§P real cases for the season PLUS read-only synthesized cases bridging the existing
        // imported source bookings (so Requests isn't empty when 26/27 already has demand).
        <RequestsView
          cases={[...scopeRequestsToSeason(bookingRequests, seasonId), ...synthesizeCasesFromBookings(areaBookings)]}
          bookings={areaBookings}
          seasonId={seasonId}
          readOnly={readOnly}
          onNewBooking={() => setShowNewBooking(true)}
          caseActions={readOnly ? null : {
            // Pass 62 — actor-context-bound conversion writers (owner/admin/ops authorized; fail-closed otherwise).
            // A confirmed case's operational child bookings then flow through the SAME canonical scheduling pipeline.
            confirmAndCreate: (caseObj) => changeCaseStage(db, caseObj, 'confirmed', { userData, organizationId: orgId, actorUserId: currentUser?.uid || '', actorName: userData?.name || userData?.displayName || '' }),
            createMissing: (caseObj) => materializeMissingCaseBookings(db, caseObj, { userData, organizationId: orgId, actorUserId: currentUser?.uid || '', actorName: userData?.name || userData?.displayName || '' }),
          }}
          organizationId={orgId}
          organization={selectedOrganization}
          seasonMembers={wd.seasonMembers}
          openCaseId={openCaseId}
          onCaseOpened={() => setOpenCaseId('')}
          onOpenBooking={setDetailBooking}
          onOpenSchedule={openSchedule}
          intakes={sourceIntakes}
          intakeActions={readOnly ? null : {
            // Pass 64 — source-intake writers, actor-authorized (owner/admin/ops). Conversion resolves client
            // identity against the live Pass-63 evidence index and creates ONE stage=inquiry case (0 bookings).
            newIntakeId,
            create: (intake) => createSourceIntake(db, intake, intakeCtx()),
            ignore: (intake) => ignoreSourceIntake(db, intake, intakeCtx()),
            convert: (intake) => convertIntakeToBookingCase(db, intake, { ...intakeCtx(), identityIndex: buildClientIdentityIndex([...bookingRequests, ...wd.seasonBookings], { organizationId: orgId }) }),
          }}
        />
      ) : view === 'calendar' ? (
        // §1/§3/§6 preserve the existing Bookings & Availability design; feed it realtime,
        // season-scoped data, a season-aware initial date, read-only gating, and the auxiliary
        // requests/availability so nothing is silently dropped.
        <OrgBookingsCRM
          onSelectInstructor={onSelectInstructor}
          providedBookings={areaBookings}
          providedMembers={wd.seasonMembers}
          providedRequests={readOnly ? [] : scopeRequestsToSeason(bookingRequests, seasonId, { timeZone: orgTz })}
          providedSlots={readOnly ? [] : availabilitySlots}
          selectedSeasonId={seasonId}
          currentSeasonId={currentSeasonId}
          timeZone={orgTz}
          initialDate={calendarInitialDate}
          readOnly={readOnly}
          onViewDay={(dateKey) => { setSelectedDate(wallClockToInstant(dateKey, '12:00', orgTz)); setView('day'); setSecondary(''); }}
        />
      ) : view === 'day' ? (
        <div>
          {/* §1 keep season+date one context: a picked date outside the season snaps in. */}
          <DateNav date={dayDate} timeZone={orgTz} onChange={(d) => setSelectedDate(clampDateToSeason(d, wd.dateRange, { timeZone: orgTz }))} />
          <DayOps
            seasonBookings={areaBookings}
            seasonMembers={wd.seasonMembers}
            seasonId={seasonId}
            organization={selectedOrganization}
            date={dayDate}
            readOnly={readOnly}
            conflicts={conflicts}
            onOpenBooking={setDetailBooking}
          />
        </div>
      ) : view === 'week' ? (
        <div>
          <WeekNav anchor={weekAnchorDate} timeZone={orgTz} onChange={(d) => setWeekAnchor(clampDateToSeason(d, wd.dateRange, { timeZone: orgTz }))} />
          <WeekOps
            seasonBookings={areaBookings}
            seasonMembers={wd.seasonMembers}
            weekStart={weekAnchorDate}
            organization={selectedOrganization}
            timeZone={orgTz}
            conflicts={conflicts}
            onOpenBooking={setDetailBooking}
          />
        </div>
      ) : view === 'season' ? (
        <SeasonOverview
          bookings={areaBookings}
          instructors={seasonInstructors}
          initialStartDate={wd.dateRange?.start || null}
          timeZone={orgTz}
          hosted
        />
      ) : (
        <UnassignedQueue
          queue={areaUnassigned}
          seasonMembers={wd.seasonMembers}
          seasonId={seasonId}
          organization={selectedOrganization}
          readOnly={readOnly}
          onOpenBooking={setDetailBooking}
          highlightBookingId={highlightBookingId}
        />
      )}

      {showAddWork && !readOnly && (
        <AddWorkModal
          organizationId={orgId}
          organization={selectedOrganization}
          seasonId={seasonId}
          seasonMembers={wd.seasonMembers}
          defaultDate={zonedDateKey(dayDate instanceof Date ? dayDate : new Date(), orgTz)}
          areas={areas}
          serviceLocations={serviceLocations}
          meetingPoints={meetingPoints}
          defaultOperatingAreaId={defaultAreaForNew}
          onClose={() => setShowAddWork(false)}
        />
      )}

      {showNewBooking && !readOnly && (
        <NewBookingWizard
          organizationId={orgId}
          organization={selectedOrganization}
          timeZone={orgTz}
          seasonId={seasonId}
          bookings={bookings}
          members={members}
          seasonMembers={wd.seasonMembers}
          areas={areas}
          serviceLocations={serviceLocations}
          meetingPoints={meetingPoints}
          defaultOperatingAreaId={defaultAreaForNew}
          onClose={() => setShowNewBooking(false)}
          onCreated={() => setView('requests')}
        />
      )}

      {detailBooking && (
        <BookingDetailModal
          booking={detailBooking}
          organizationId={orgId}
          organization={selectedOrganization || {}}
          member={wd.seasonMembers.find((m) => m.userId === detailBooking.instructorId
            || (detailBooking.instructorAuthUserId && m.authUserId === detailBooking.instructorAuthUserId)) || null}
          seasonId={seasonId}
          onClose={() => setDetailBooking(null)}
          onChanged={() => setDetailBooking(null)}
          onOpenCase={(caseId) => { setDetailBooking(null); setOpenCaseId(caseId); setView('requests'); setSecondary(''); }}
          onStartLesson={readOnly ? null : startLessonForBooking}
          readOnly={readOnly}
          locationConfig={{ areas, serviceLocations, meetingPoints }}
        />
      )}
    </div>
  );
}

// Pass 82C.2 §8 — all operational date navigation is in the SELECTED ORGANIZATION's timezone: the input value is the
// org calendar day, the picker parses back to an org-local instant, and prev/next shift whole org-local days. SAS
// passes 'Asia/Tokyo' (identical to the former JST behaviour); a non-JST tenant navigates its own local days.
function DateNav({ date, onChange, timeZone }) {
  const key = zonedDateKey(date instanceof Date ? date : new Date(date || Date.now()), timeZone);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 10px' }}>
      <button onClick={() => onChange(zonedShiftDays(date, -1, timeZone))} style={pill(false)}>‹ Prev</button>
      <input type="date" value={key} onChange={(e) => e.target.value && onChange(wallClockToInstant(e.target.value, '12:00', timeZone))} style={{ padding: '6px 8px', borderRadius: 8, border: `1px solid ${V.border}`, fontSize: 13 }} />
      <button onClick={() => onChange(zonedShiftDays(date, 1, timeZone))} style={pill(false)}>Next ›</button>
    </div>
  );
}

function WeekNav({ anchor, onChange, timeZone }) {
  const start = weekStartOf(anchor, timeZone);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 10px' }}>
      <button onClick={() => onChange(zonedShiftDays(start, -7, timeZone))} style={pill(false)}>‹ Prev week</button>
      <span style={{ fontSize: 12, color: V.t2 }}>Week of {zonedDateKey(start, timeZone)}</span>
      <button onClick={() => onChange(zonedShiftDays(start, 7, timeZone))} style={pill(false)}>Next week ›</button>
    </div>
  );
}
